import { useState } from 'react'
import { Alert, Box, Button, Form, Input, Stack, TextArea } from '@ui'
import { patchJSON } from '../api.js'

// Notes, editable on the website (M3). Edits stay on the site: the phone keeps its own notes
// until app sync exists (plan decision 5).
export function NotesEditor({ walk, onSaved }) {
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      onSaved(await patchJSON(`/api/walks/${walk.id}`, { notes: draft }))
      setDraft(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (draft == null) {
    return (
      <Box mb={3}>
        {walk.notes && <Box as="p" mb={1} style={{ whiteSpace: 'pre-wrap' }}>{walk.notes}</Box>}
        <Button size="sm" variant="link" p={0} onClick={() => setDraft(walk.notes ?? '')}>
          {walk.notes ? 'Edit notes' : 'Add notes'}
        </Button>
      </Box>
    )
  }
  return (
    <Form mb={3} onSubmit={(e) => { e.preventDefault(); save() }}>
      <TextArea
        rows={3} autoFocus aria-label="Notes" maxLength={10000}
        value={draft} onChange={(e) => setDraft(e.target.value)} disabled={saving}
      />
      <Stack gap={2} mt={2}>
        <Button size="sm" type="submit" disabled={saving}>Save notes</Button>
        <Button size="sm" variant="secondary" outline onClick={() => { setDraft(null); setError(null) }} disabled={saving}>Cancel</Button>
      </Stack>
      {error && <Alert variant="danger" compact mt={2} mb={0}>Couldn't save: {error}</Alert>}
    </Form>
  )
}

// Excluding a walk takes it out of every count and total (walk_groups); it stays visible.
export function ExcludeControl({ walk, onSaved }) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const save = async (excluded) => {
    setSaving(true)
    setError(null)
    try {
      onSaved(await patchJSON(`/api/walks/${walk.id}`, { excluded, exclusion_reason: excluded ? reason : null }))
      setAsking(false)
      setReason('')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box mb={3}>
      {walk.excluded_from_analysis ? (
        <Button size="sm" variant="secondary" outline onClick={() => save(false)} disabled={saving}>Count this walk again</Button>
      ) : asking ? (
        <Form display="flex" wrap gap={2} align="center" onSubmit={(e) => { e.preventDefault(); save(true) }}>
          <Input
            size="sm" style={{ maxWidth: '20rem' }} autoFocus placeholder="Reason (optional)" aria-label="Reason for excluding"
            value={reason} onChange={(e) => setReason(e.target.value)} disabled={saving}
          />
          <Button size="sm" variant="danger" type="submit" disabled={saving}>Exclude from totals</Button>
          <Button size="sm" variant="secondary" outline onClick={() => setAsking(false)} disabled={saving}>Cancel</Button>
        </Form>
      ) : (
        <Button size="sm" variant="secondary" outline onClick={() => setAsking(true)}>Exclude from totals…</Button>
      )}
      {error && <Alert variant="danger" compact mt={2} mb={0}>Couldn't save: {error}</Alert>}
    </Box>
  )
}
